// The public front door served at the apex (the default "Welcome" tenant) and
// at any host that maps to no tenant. It renders for a visitor with no session
// and no tenant data, so it must not depend on any authenticated query, role,
// realtime, or feature flag — the one authenticated call is the workspace list
// below, whose absence simply hides the "Go to your workspace" action.
import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { ArrowRight, CheckCircle2, LogOut, MessageSquare, Radio, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/shared/Logo";
import {
  useSwitchableTenants,
  useSwitchTenant,
  useSetPreferredTenant,
  useCookieDomain,
  tenantReachable,
} from "@/hooks/useTenant";
import { currentAccessToken, supabase } from "@/integrations/db/client";
import { cn } from "@/lib/utils";
import { APP_NAME, EVERON_LOGO } from "@/lib/constants";

/**
 * Same-origin scheme and port, target host swapped — so the link works in
 * production (https, standard port) and in local dev (http, :8080) alike,
 * rather than hardcoding https. Only for the case the tenant switch cannot
 * carry a session into: the switch builds its own URL.
 */
function hostUrl(primaryHost: string): string {
  const port = typeof window !== "undefined" && window.location.port ? `:${window.location.port}` : "";
  const protocol = typeof window !== "undefined" ? window.location.protocol : "https:";
  return `${protocol}//${primaryHost}${port}/`;
}

const FEATURES = [
  {
    icon: CheckCircle2,
    title: "Six states, one glance.",
    body: "Colour-coded statuses, so a queue reads clearly from To Do through Done.",
  },
  {
    icon: MessageSquare,
    title: "Threaded comments.",
    body: "Discuss right on the ticket, not in a side channel that disappears.",
  },
  {
    icon: Radio,
    title: "Live by default.",
    body: "Status moves the moment someone else changes it — nobody hits refresh.",
  },
];

/** Illustrative rows for the decorative preview — not real data. */
const ACTIVE_WORK = [
  { id: "#241", task: "Renew vendor contract", pill: "Blocked" as const },
  { id: "#238", task: "Access review for Q3", pill: "Progress" as const },
  { id: "#402", task: "Fix broken checkout link", pill: "Progress" as const },
  { id: "#398", task: "Update employee handbook", pill: "Todo" as const },
];
const QUEUE_ROW = { id: "#256", task: "Waiting on Finance", pill: "Todo" as const };
const BLOCKED_ROWS = [
  { id: "#241", task: "Renew vendor contract" },
  { id: "#229", task: "Vendor signature pending" },
];

const PILL_STYLES: Record<"Todo" | "Progress" | "Blocked", string> = {
  Todo: "bg-blue-100 text-blue-900",
  Progress: "bg-amber-100 text-amber-900",
  Blocked: "bg-red-100 text-red-900",
};

const Landing = () => {
  // Empty unless a session cookie is present — no session, no action.
  const tenants = useSwitchableTenants();
  const switchTenant = useSwitchTenant();
  const setPreferred = useSetPreferredTenant();
  const cookieDomain = useCookieDomain();
  // Seeded from the stored token rather than a query: the workspace list
  // arrives a round-trip later, and keying the way-in button on that would
  // flash it at someone who is already signed in. Kept in step afterwards
  // because signing out happens ON this page and never leaves it — the
  // button that did it has to become the way back in.
  const [signedIn, setSignedIn] = useState(() => currentAccessToken() !== null);
  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((_event, session) => setSignedIn(session !== null));
    return () => data.subscription.unsubscribe();
  }, []);
  // The decorative preview's fake urlbar reads as this host's own address
  // rather than a made-up domain.
  const previewHost = typeof window !== "undefined" ? window.location.host : "backlog.app";

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      {/* Decorative drifting aurora. Non-interactive and hidden from assistive tech. */}
      <div className="aurora" aria-hidden="true">
        <div className="aurora__grid" />
        <div className="aurora__blob aurora__blob--a" />
        <div className="aurora__blob aurora__blob--b" />
        <div className="aurora__blob aurora__blob--c" />
      </div>

      <div className="relative mx-auto flex min-h-screen w-full max-w-[1360px] flex-col px-[clamp(20px,5vw,72px)]">
        <header className="flex items-center justify-between pt-[26px]">
          <div className="flex items-center gap-2.5">
            <Logo className="h-8 w-8" />
            <span className="text-lg font-semibold tracking-tight text-foreground">Backlog</span>
          </div>
          {/* Sign-out belongs top right, where every workspace keeps it, and
              this page is the one place a signed-in person can be with no app
              chrome around them: the session is real but no workspace is open,
              so without this the only way out is to guess a URL. It stays out
              of the copy column, which answers "where am I going" rather than
              "who am I". */}
          {signedIn && (
            <Button variant="ghost" size="sm" className="gap-2" onClick={() => void supabase.auth.signOut()}>
              <LogOut className="h-4 w-4" />
              Log out
            </Button>
          )}
        </header>

        <main className="grid flex-1 items-center gap-[clamp(28px,4vw,60px)] py-[clamp(28px,5vh,64px)] lg:grid-cols-[1.05fr_0.95fr]">
          {/* Copy column */}
          <div>
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[hsl(199,70%,70%,0.4)] bg-[hsl(199,90%,92%)] px-3 py-1.5 font-['IBM_Plex_Mono',ui-monospace,monospace] text-[0.72rem] font-medium uppercase tracking-[0.08em] text-[hsl(208,100%,24%)] dark:border-[hsl(199,60%,40%,0.4)] dark:bg-[hsl(199,60%,20%)] dark:text-[hsl(199,80%,80%)]">
              <span className="eyebrow-dot h-1.5 w-1.5 rounded-full bg-[hsl(var(--ring))]" aria-hidden="true" />
              You&apos;ve reached the front door
            </div>

            <h1 className="mb-5 text-balance font-['Fraunces',ui-serif,Georgia,serif] text-[clamp(2.8rem,5.2vw,4.5rem)] font-medium leading-[1.03] tracking-tight text-foreground">
              The backlog your team{" "}
              <span className="bg-gradient-to-r from-[hsl(205,90%,55%)] to-[hsl(190,85%,50%)] bg-clip-text italic text-transparent">
                actually
              </span>{" "}
              opens.
            </h1>

            <p className="mb-8 max-w-[46ch] text-balance text-[1.15rem] leading-relaxed text-muted-foreground">
              One shared queue for every request, comment, and status change. Sign in here and we&apos;ll take you to
              your company&apos;s backlog — or open its own address directly, if you have the link.
            </p>

            {/* Signed out, the way in takes the slot the workspace buttons
                will occupy once there IS a session: the page keeps one place
                where "where do I go from here" is answered, and the answer
                changes rather than moving. */}
            {!signedIn && (
              <div className="mb-10">
                <Button asChild className="justify-between gap-3 sm:w-auto">
                  <NavLink to="/login">
                    Log in
                    <ArrowRight className="h-4 w-4" />
                  </NavLink>
                </Button>
              </div>
            )}

            {tenants.length > 0 && (
              <div className="mb-10 space-y-3">
                <p className="text-sm font-medium text-muted-foreground">Go to your workspace</p>
                {/* Going somewhere is a tenant SWITCH, not a navigation. The
                    session's tenant_id claim is what the data is read under, so
                    a plain link to another workspace's host lands you there
                    reading the one you came from, under the wrong name. The
                    switch re-mints first (membership re-checked server-side)
                    and redirects itself, which is also how the switcher in the
                    app header does it.

                    That only carries the session when the cookie's parent
                    domain reaches the target host. Where it does not — a
                    workspace on its own vanity domain, or any local setup,
                    since browsers reject a `Domain=localhost` cookie outright —
                    the button still goes there, just as a plain navigation:
                    arriving at your own workspace's sign-in page is a worse
                    answer than staying signed in, and a much better one than a
                    button that does nothing. */}
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                  {tenants.map((t) => {
                    const reachable = tenantReachable(t.primary_host, cookieDomain);
                    const variant = t.primary_host ? "default" : "outline";
                    return (
                      // One pill, two targets — a split button. The star sits
                      // ON the button's surface rather than beside it, but it
                      // is a SIBLING of the workspace action, not a child:
                      // interactive content nested inside a <button> is invalid
                      // HTML, and the nested label would be swallowed into the
                      // outer button's accessible name ("XBG Backlog Make XBG
                      // your default workspace"). As siblings, a click on the
                      // star cannot reach the workspace action at all — there
                      // is no propagation to stop, which is a stronger
                      // guarantee than stopping it.
                      <div
                        key={t.id}
                        className="inline-flex items-stretch overflow-hidden rounded-md [&>button]:rounded-none"
                      >
                        <Button
                          variant={variant}
                          disabled={!t.primary_host || switchTenant.isPending}
                          title={
                            t.primary_host
                              ? reachable
                                ? undefined
                                : "You'll be asked to sign in when you get there"
                              : "This workspace isn't reachable yet"
                          }
                          className="flex-1 justify-between gap-3"
                          onClick={() => {
                            if (!t.primary_host) return;
                            if (reachable) switchTenant.mutate(t);
                            else window.location.assign(hostUrl(t.primary_host));
                          }}
                        >
                          {t.name} {APP_NAME}
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                        {/* The seam, in whichever ink the pill is wearing: the
                            two halves do different things and the hover states
                            land separately, so an unbroken fill would misread
                            as one target with a decorative icon. */}
                        <span
                          aria-hidden="true"
                          className={cn("w-px self-stretch", t.primary_host ? "bg-primary-foreground/25" : "bg-border")}
                        />
                        {/* This is the moment the preference is worth setting —
                            someone who just signed in at the shared door and is
                            about to pick the same workspace they always pick. */}
                        <Button
                          variant={variant}
                          size="icon"
                          className="shrink-0"
                          disabled={setPreferred.isPending}
                          aria-pressed={t.preferred}
                          aria-label={
                            t.preferred
                              ? `${t.name} is your default workspace, click to clear`
                              : `Make ${t.name} your default workspace`
                          }
                          title={t.preferred ? "Your default workspace" : "Make default"}
                          onClick={() => setPreferred.mutate(t.preferred ? null : t.id)}
                        >
                          <Star
                            className={cn(
                              "h-4 w-4",
                              // On the filled pill the star has to read against
                              // the brand ink, so "set" is the solid shape and
                              // "unset" is the same shape at half strength —
                              // the ring colour it wears elsewhere would
                              // disappear into the fill.
                              t.preferred ? "fill-current" : t.primary_host ? "opacity-50" : "text-muted-foreground/60",
                            )}
                          />
                        </Button>
                      </div>
                    );
                  })}
                </div>
                {tenants.length > 1 && (
                  <p className="text-xs text-muted-foreground">
                    Star one to skip this step next time you sign in here.
                  </p>
                )}
              </div>
            )}

            <div className="grid gap-4 border-t border-border pt-6 sm:max-w-[44ch]">
              {FEATURES.map((feature) => (
                <div key={feature.title} className="flex items-start gap-3 text-sm text-muted-foreground">
                  <feature.icon
                    className="mt-0.5 h-[18px] w-[18px] shrink-0 text-[hsl(var(--ring))]"
                    aria-hidden="true"
                  />
                  <p>
                    <span className="font-semibold text-foreground">{feature.title}</span> {feature.body}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Angled product preview — purely decorative, illustrates a member's
              dashboard (not admin). Hidden below lg where there's no room for
              the tilt to read as anything but clutter. */}
          <div className="hidden lg:flex lg:justify-center" aria-hidden="true" style={{ perspective: "2600px" }}>
            <div className="relative w-full max-w-[640px] [transform-style:preserve-3d] [transform:rotateY(-13deg)_rotateX(6deg)_rotateZ(1deg)] transition-transform duration-500 ease-out hover:[transform:rotateY(-9deg)_rotateX(4deg)_rotateZ(0.5deg)]">
              {/* A second, receded card gives the stack real depth at the tilt angle. */}
              <div
                className="absolute inset-3 -z-10 rounded-2xl border border-border bg-card/80 [transform:translateZ(-40px)_scale(0.97)]"
                style={{ boxShadow: "0 30px 70px -25px hsl(var(--primary) / 0.4)" }}
              />

              <div className="absolute -left-10 -top-8 z-10 rounded-xl border border-border bg-card px-4 py-2.5 shadow-xl [transform:translateZ(90px)]">
                <div className="font-['Fraunces',ui-serif,Georgia,serif] text-xl leading-none text-[hsl(var(--ring))]">
                  14
                </div>
                <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Active today</div>
              </div>

              <div
                className="overflow-hidden rounded-2xl border border-border bg-card [transform:translateZ(0px)]"
                style={{
                  boxShadow:
                    "0 60px 120px -30px hsl(var(--primary) / 0.45), 0 25px 50px -15px hsl(var(--primary) / 0.3), 0 0 0 1px hsl(var(--primary) / 0.06)",
                }}
              >
                <div className="flex items-center gap-2 border-b border-border bg-muted/60 px-3.5 py-2.5">
                  <div className="flex gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-[#f5766b]" />
                    <span className="h-2.5 w-2.5 rounded-full bg-[#f6bd51]" />
                    <span className="h-2.5 w-2.5 rounded-full bg-[#59c771]" />
                  </div>
                  <div className="flex-1 rounded-md border border-border bg-card py-1 text-center font-mono text-[10px] text-muted-foreground">
                    yourteam.{previewHost}
                  </div>
                </div>

                <div className="bg-[#f7f8fa] p-4 text-[#1c2433]">
                  <div className="mb-3 flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2 text-[11px] shadow-sm">
                    <span className="font-extrabold text-[#0f1b2d]">Backlog</span>
                    <span className="rounded-md border border-[#e3e7ed] bg-[#f1f3f6] px-2 py-1 font-semibold text-[#3a4453]">
                      Engineering ⌄
                    </span>
                    <div className="flex gap-3 font-bold text-[#7c8798]">
                      <span className="text-[#123a63]">Dashboard</span>
                      <span>Backlog</span>
                      <span>People</span>
                      <span>Teams</span>
                    </div>
                    <span className="grid h-5 w-5 place-items-center rounded-full bg-gradient-to-br from-[#3f7fc4] to-[#7a5fd0] text-[9px] font-extrabold text-white">
                      MM
                    </span>
                  </div>

                  <div className="mb-1 text-sm font-extrabold text-[#0f1b2d]">Good afternoon, Mel</div>
                  <div className="mb-3 text-[10px] text-[#7c8798]">
                    {ACTIVE_WORK.length} active · 1 in your teams&apos; queues · {BLOCKED_ROWS.length} blocked
                  </div>

                  <div className="grid grid-cols-[1.55fr_1fr] gap-3">
                    <div className="rounded-lg border border-[#e7eaf0] bg-white p-2.5">
                      <div className="mb-2 flex items-center justify-between text-[10px] font-extrabold text-[#0f1b2d]">
                        My Active Work
                        <span className="rounded bg-[#eef1f5] px-1.5 py-0.5 text-[9px] font-semibold text-[#5b6577]">
                          {ACTIVE_WORK.length}
                        </span>
                      </div>
                      {ACTIVE_WORK.map((row) => (
                        <div key={row.id} className="flex items-center justify-between gap-2 py-1 text-[10px]">
                          <span className="w-8 flex-none font-mono text-[9px] text-[#a7b0bd]">{row.id}</span>
                          <span className="flex-1 truncate text-[#29344a]">{row.task}</span>
                          <span
                            className={`flex-none rounded px-1.5 py-0.5 text-[8.5px] font-extrabold ${PILL_STYLES[row.pill]}`}
                          >
                            {row.pill === "Progress" ? "In Progress" : row.pill === "Todo" ? "To Do" : "Blocked"}
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-col gap-2.5">
                      <div className="rounded-lg border border-[#e7eaf0] bg-white p-2.5">
                        <div className="mb-2 text-[10px] font-extrabold text-[#0f1b2d]">My Teams&apos; Queues</div>
                        <div className="flex items-center justify-between gap-2 text-[10px]">
                          <span className="w-8 flex-none font-mono text-[9px] text-[#a7b0bd]">{QUEUE_ROW.id}</span>
                          <span className="flex-1 truncate text-[#29344a]">{QUEUE_ROW.task}</span>
                          <span
                            className={`flex-none rounded px-1.5 py-0.5 text-[8.5px] font-extrabold ${PILL_STYLES.Todo}`}
                          >
                            To Do
                          </span>
                        </div>
                      </div>
                      <div className="rounded-lg border border-[#e7eaf0] bg-white p-2.5">
                        <div className="mb-2 text-[10px] font-extrabold text-[#0f1b2d]">Blocked</div>
                        {BLOCKED_ROWS.map((row) => (
                          <div key={row.id} className="flex items-center justify-between gap-2 py-1 text-[10px]">
                            <span className="w-8 flex-none font-mono text-[9px] text-[#a7b0bd]">{row.id}</span>
                            <span className="flex-1 truncate text-[#29344a]">{row.task}</span>
                            <span
                              className={`flex-none rounded px-1.5 py-0.5 text-[8.5px] font-extrabold ${PILL_STYLES.Blocked}`}
                            >
                              Blocked
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="absolute -right-8 bottom-8 z-10 flex items-center gap-2 rounded-xl border border-border bg-card px-3.5 py-2 text-xs font-bold text-foreground shadow-xl [transform:translateZ(90px)]">
                <span className="h-2 w-2 rounded-full bg-green-500 shadow-[0_0_0_4px_rgba(34,197,94,0.2)]" />
                Live · Mel Member
              </div>
            </div>
          </div>
        </main>

        <footer className="flex items-center justify-between border-t border-border/60 py-6 text-xs text-muted-foreground">
          <span>Backlog. Own your workspace.</span>
          <span className="flex items-center gap-1.5">
            <span>Powered by</span>
            <img src={EVERON_LOGO} alt="" className="h-4 w-4" />
            <span className="font-medium">Everon</span>
          </span>
        </footer>
      </div>
    </div>
  );
};

export default Landing;
